import classNames from 'classnames';
import type { IntlKey } from 'locales';
import { FC, KeyboardEvent, ReactNode, useCallback } from 'react';
import { useIntl } from 'react-intl';
import { amountDefaultAccelerators } from 'utils/amount';

import { NumberFormatter } from '../NumberFormatter/NumberFormatter';
import { CurrencySymbol } from './CurrencySymbol';
export interface INewAmountInputProps {
  id?: string;
  e2e: string;
  value?: Nullable<number>;
  precision?: number;
  placeholderKey?: IntlKey;
  onValueChanged?: (value: Nullable<number>) => void;
  currency: string;
  hasError?: boolean;
  decimalPadding?: boolean;
  isDisabled?: boolean;
  tabIndex?: number;
  prepend?: ReactNode;
  className?: string;
  hidePlaceholder?: boolean;
  onKeyDown?: (e: KeyboardEvent) => void;
  isFocused?: boolean;
  isSelectAll?: boolean;
  unitPosition?: 'start' | 'end';
  textAlignment?: 'start' | 'end';
}

export const NewAmountInput: FC<INewAmountInputProps> = ({
  value,
  currency,
  e2e,
  onValueChanged,
  placeholderKey,
  precision,
  hasError,
  decimalPadding,
  isDisabled,
  id,
  tabIndex,
  prepend,
  className,
  hidePlaceholder,
  onKeyDown,
  isFocused,
  isSelectAll,
  unitPosition = 'start',
  textAlignment = 'end',
}) => {
  const { formatMessage } = useIntl();
  const onBlur = useCallback(
    (val: number | undefined) => {
      const newValue: Nullable<number> = val !== undefined ? val : null;
      if ((newValue ?? null) !== (value ?? null) && onValueChanged) {
        onValueChanged(newValue);
      }
    },
    [onValueChanged, value],
  );
  const placeholderLabel = placeholderKey !== undefined ? formatMessage({ id: placeholderKey }) : undefined;

  return (
    <div className="with-unit-component input-group" data-e2e="NotionalAmount-with-unit">
      {prepend}
      {unitPosition === 'start' && (
        <span className="text-secondary input-group-text align-self-stretch h-auto">
          <CurrencySymbol code={currency} />
        </span>
      )}
      <NumberFormatter
        value={value}
        e2e={e2e}
        onBlur={onBlur}
        placeholder={hidePlaceholder ? '' : placeholderLabel}
        className={classNames(textAlignment === 'end' ? 'text-end' : 'text-start', className)}
        precision={precision}
        decimalPadding={decimalPadding}
        hasError={hasError}
        accelerators={amountDefaultAccelerators}
        incrementStep={1_000_000}
        isDisabled={isDisabled}
        id={id}
        tabIndex={tabIndex}
        onKeyDown={onKeyDown}
        isFocused={isFocused}
        isSelectAll={isSelectAll}
      />
      {unitPosition === 'end' && (
        <span className="text-secondary input-group-text align-self-stretch h-auto">
          <CurrencySymbol code={currency} />
        </span>
      )}
    </div>
  );
};
NewAmountInput.displayName = 'NewAmountInput';
